import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FiEdit2,
  FiExternalLink,
  FiFileText,
  FiPlus,
  FiTrash2,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { Button, Select, Table } from "components/common";
import { PageHeader, useToast } from "components/admin";
import { ASSET_BASE_URL } from "constants/apiEndpoints";
import {
  deleteShareholderDocument,
  getAdminShareholderDocumentCategories,
  getAdminShareholderDocuments,
} from "apis/shareholderDocuments";
import "../../../styles/admin-shared.scss";
import "./shareholderDocuments.scss";

const PAGE_SIZE = 10;

const normalizeList = (response) => {
  const payload = response?.data ?? response ?? {};
  const items = Array.isArray(payload)
    ? payload
    : payload.items ?? payload.data ?? [];
  return {
    items,
    total:
      payload.pagination?.totalItems ??
      payload.totalItems ??
      payload.total ??
      items.length,
    pageSize: payload.pagination?.pageSize ?? payload.pageSize ?? PAGE_SIZE,
  };
};

const resolveDocumentUrl = (url) => {
  if (!url || /^(https?:)?\/\//i.test(url)) return url;
  return `${ASSET_BASE_URL.replace(/\/$/, "")}/${url.replace(/^\//, "")}`;
};

export default function ShareholderDocumentsList() {
  const navigate = useNavigate();
  const toast = useToast();
  const [page, setPage] = useState(1);
  const [categoryId, setCategoryId] = useState("");
  const [categories, setCategories] = useState([]);
  const [result, setResult] = useState({
    items: [],
    total: 0,
    pageSize: PAGE_SIZE,
  });
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    getAdminShareholderDocuments({
      pageIndex: page,
      pageSize: PAGE_SIZE,
      languageCode: "vi",
      ...(categoryId ? { categoryId } : {}),
    })
      .then((response) => setResult(normalizeList(response)))
      .catch(() => toast.error("Không thể tải tài liệu cổ đông."))
      .finally(() => setLoading(false));
  }, [categoryId, page, toast]);

  useEffect(() => {
    getAdminShareholderDocumentCategories({
      pageIndex: 1,
      pageSize: 100,
      languageCode: "vi",
    })
      .then((response) => {
        const payload = response?.data ?? response ?? {};
        setCategories(
          Array.isArray(payload) ? payload : payload.items ?? payload.data ?? [],
        );
      })
      .catch(() => toast.error("Không thể tải danh mục tài liệu."));
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  const remove = async (id) => {
    if (!window.confirm("Bạn chắc chắn muốn xóa tài liệu này?")) return;
    try {
      await deleteShareholderDocument(id);
      toast.success("Đã xóa tài liệu.");
      load();
    } catch {
      toast.error("Không thể xóa tài liệu.");
    }
  };

  const categoryOptions = useMemo(
    () => [
      { value: "", label: "Tất cả danh mục" },
      ...categories.map((category) => ({
        value: category.id ?? category.Id,
        label:
          category.name ??
          category.Name ??
          category.title ??
          category.code ??
          category.Code,
      })),
    ],
    [categories],
  );

  const columns = [
    {
      header: "STT",
      render: (_row, index) => (page - 1) * PAGE_SIZE + index + 1,
    },
    {
      header: "Tài liệu",
      render: (row) => (
        <div className="document-admin-name">
          <span className="document-admin-name__icon"><FiFileText /></span>
          <div>
            <strong>{row.title || row.Title || row.fileName || row.FileName}</strong>
            <small>{row.description || row.Description || row.fileUrl || row.FileUrl}</small>
          </div>
        </div>
      ),
    },
    {
      header: "Danh mục",
      render: (row) =>
        row.categoryName ||
        row.CategoryName ||
        row.category?.name ||
        row.category?.Name ||
        "—",
    },
    {
      header: "Năm",
      render: (row) => row.publishedYear ?? row.PublishedYear ?? "—",
    },
    {
      header: "Trạng thái",
      render: (row) => {
        const active = row.isActive ?? row.IsActive;
        return (
          <span className={`status-badge ${active ? "active" : "inactive"}`}>
            {active ? "Hiển thị" : "Đã ẩn"}
          </span>
        );
      },
    },
    {
      header: "Thao tác",
      render: (row) => {
        const id = row.id ?? row.Id;
        const url = row.fileUrl ?? row.FileUrl;
        return (
          <div className="action-buttons">
            {url && (
              <a
                className="icon-btn"
                href={resolveDocumentUrl(url)}
                target="_blank"
                rel="noreferrer"
                title="Mở tài liệu"
              >
                <FiExternalLink />
              </a>
            )}
            <button
              type="button"
              className="icon-btn icon-btn--edit"
              title="Chỉnh sửa"
              onClick={() =>
                navigate(`/admin/shareholder-documents/edit/${id}`)
              }
            >
              <FiEdit2 />
            </button>
            <button
              type="button"
              className="icon-btn icon-btn--delete"
              title="Xóa"
              onClick={() => remove(id)}
            >
              <FiTrash2 />
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <div className="admin-page shareholder-admin">
      <PageHeader
        title="Tài liệu cổ đông"
        subtitle="Quản lý báo cáo tài chính, quản trị công ty và báo cáo thường niên."
        action={
          <Button onClick={() => navigate("/admin/shareholder-documents/create")}>
            <FiPlus /> Thêm tài liệu
          </Button>
        }
      />
      <div className="shareholder-document-toolbar">
        <Select
          label="Lọc theo danh mục"
          value={categoryId}
          options={categoryOptions}
          onChange={(event) => {
            setCategoryId(event.target.value);
            setPage(1);
          }}
        />
      </div>
      <Table
        columns={columns}
        data={result.items}
        loading={loading}
        emptyMessage="Chưa có tài liệu trong danh mục này."
        pagination={{
          currentPage: page,
          totalPages: Math.max(1, Math.ceil(result.total / result.pageSize)),
          onPageChange: setPage,
        }}
      />
    </div>
  );
}
